// Filename: /src/components/CommentSection/CommentSection.js

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext'; // Fix: Adjust path if needed
import { fetchCommentsByThread, postComment } from './commentApi';
import { ChatBubbleBottomCenterTextIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import Comment from './Comment';
import CommentForm from './CommentForm';
import LoginPrompt from './LoginPrompt';

// --- Rate Limiting Helper Functions ---

const checkGuestLimit = (limit) => {
    if (limit <= 0) return true; // A limit of 0 or less means unlimited.
    const key = 'guest_comments';
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    try {
        const timestamps = JSON.parse(localStorage.getItem(key) || '[]');
        const recentTimestamps = timestamps.filter(ts => ts > oneDayAgo);
        localStorage.setItem(key, JSON.stringify(recentTimestamps));
        return recentTimestamps.length < limit;
    } catch { return true; }
};

const recordGuestComment = () => {
    const key = 'guest_comments';
    try {
        const timestamps = JSON.parse(localStorage.getItem(key) || '[]');
        timestamps.push(Date.now());
        localStorage.setItem(key, JSON.stringify(timestamps));
    } catch {}
};

const checkUserRateLimit = (threadId, userId, intervalMinutes) => {
    if (intervalMinutes <= 0) return true; // A limit of 0 or less means no rate limit.
    const key = `user_comments_${userId}_${threadId}`;
    const coolDownPeriod = intervalMinutes * 60 * 1000;
    try {
        const lastCommentTimestamp = JSON.parse(localStorage.getItem(key) || '0');
        return Date.now() - lastCommentTimestamp > coolDownPeriod;
    } catch { return true; }
};

const recordUserComment = (threadId, userId) => {
    const key = `user_comments_${userId}_${threadId}`;
    try {
        localStorage.setItem(key, JSON.stringify(Date.now()));
    } catch {}
};

// --- Main Component ---

const CommentSection = ({ 
    className = '',
    threadId, 
    showCommentsWhenLoggedOut = true,
    guestCommentsPerDay = 3,
    userCommentIntervalMinutes = 3
}) => {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [limitError, setLimitError] = useState('');

    const loadComments = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const fetchedComments = await fetchCommentsByThread(threadId);
            setComments(fetchedComments);
        } catch (err) {
            setError('Failed to load comments. Please try again.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [threadId]);

    useEffect(() => {
        if (threadId && (showCommentsWhenLoggedOut || user)) {
            loadComments();
        }
    }, [threadId, user, showCommentsWhenLoggedOut, loadComments]);
    
    // Generic handler to wrap API calls with rate-limiting logic
    const handlePost = async (apiCall) => {
        setLimitError('');
        if (user) {
            if (!checkUserRateLimit(threadId, user.id, userCommentIntervalMinutes)) {
                const message = userCommentIntervalMinutes === 1 
                    ? "You can post one comment per minute on this thread."
                    : `You can post one comment every ${userCommentIntervalMinutes} minutes on this thread.`;
                setLimitError(message);
                return null;
            }
        } else { // This is a guest
             if (!checkGuestLimit(guestCommentsPerDay)) {
                setLimitError(`Guests can post ${guestCommentsPerDay} comments per day. Please create an account to continue.`);
                return null;
            }
        }

        try {
            const newComment = await apiCall();
            if (user) recordUserComment(threadId, user.id);
            else recordGuestComment();
            return newComment;
        } catch (err) {
            setError('Could not post comment. Please try again.');
            console.error(err);
            return null;
        }
    };

    const handlePostNewComment = async (content) => {
        const newComment = await handlePost(() => postComment({ threadId, content, user }));
        if (newComment) setComments(prev => [newComment, ...prev]);
    };

    const handlePostReply = async ({ content, parentId, replyingTo }) => {
        const newReply = await handlePost(() => postComment({ threadId, content, parentId, replyingTo, user }));
        if (newReply) {
            setComments(currentComments =>
                currentComments.map(c => 
                    c.id === parentId ? { ...c, replies: [...(c.replies || []), newReply] } : c
                )
            );
        }
    };
  
    const canShowSection = showCommentsWhenLoggedOut || user;

    return (
        <div className={"max-w-3xl mx-auto w-full space-y-2 mt-10" + (className ? ` ${className}` : '')}>
            <div className="bg-gray-900/50 border border-gray-800 rounded-lg">
                <button
                    onClick={() => setIsOpen(prev => !prev)}
                    className="w-full flex justify-between items-center p-3 text-left hover:bg-gray-800/60"
                >
                    <div className="flex items-center gap-3">
                        <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                        <h2 className="text-lg font-semibold text-gray-200">Community Comments</h2>
                    </div>
                    <ChevronDownIcon
                        className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    />
                </button>

                {isOpen && canShowSection && (
                    <div className="border-t border-gray-800 p-4 space-y-4">
                        {user ? (
                            <CommentForm onSubmit={handlePostNewComment} />
                        ) : (
                            <LoginPrompt />
                        )}
                        
                        {limitError && <p className="text-sm text-center text-yellow-500 py-2">{limitError}</p>}
                        {error && <p className="text-center text-red-400">{error}</p>}
                        {isLoading && <p className="text-center text-gray-400 py-4">Loading comments...</p>}
                        
                        {!isLoading && !error && (
                            comments.length > 0 ? (
                                <div className="divide-y divide-gray-800">
                                    {comments.map(comment => (
                                        <Comment key={comment.id} comment={comment} onPostReply={handlePostReply} />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-gray-500 py-4 text-sm">Be the first to share your thoughts!</p>
                            )
                        )}
                    </div>
                )}
                
                {isOpen && !canShowSection && (
                    <div className="border-t border-gray-800 p-4">
                        <LoginPrompt />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommentSection;